// A link action arrives as an `oobCode` in the URL of `/emailAction`: a
// registration link waiting to be exchanged for a session, or a password reset
// waiting to be completed. Either way the code is a request to *change* the
// session rather than to use the one already here, so `AuthRoute` must not
// redirect a signed-in player away from it before the screen that spends it has
// had its turn.
//
// Without that, a player whose sign-in went stale had nowhere to go: the
// expired-session screen sends them a fresh link, they click it, and
// `AuthRoute` sees a live Firebase session and a live token and sends them to
// /game before `EmailLoginRedirector` ever mounts. The code is never exchanged,
// `auth_time` never moves, and the registration form refuses the password
// again — with no way off that machine but clearing site data.
//
// What the route needs to know is not "is there a code" but "is this one still
// being spent". The redirect is also how a *finished* exchange gets the player
// where they are going, so suppressing it for the whole visit would strand them
// on a loading screen instead. The record below is the difference between the
// two, and it holds until the exchange has settled — not until it has started.
// Released at the start, the mark made the gate one-shot: the next render for
// any reason at all redirected mid-exchange, tearing down the screen that was
// waiting on it and handing the player the recovery screen for a refusal that
// had already been overtaken.
//
// Settling covers a refusal as much as a success. A dead code that held the gate
// for ever would leave a signed-in player on the recovery screen with no way
// back to the game — worse than the redirect they used to get, now that leaving
// costs them their session.
//
// Module scope on purpose, and plain module state rather than React state: this
// outlives the mount that started the exchange, which is the point — React can
// rebuild the screen mid-visit, and a ref or a state value would go with it.
const spentCodes = new Set();
const listeners = new Set();

// Bumped on every change so that `useSyncExternalStore` has something to
// compare. The set itself is mutated in place, so it cannot serve as a snapshot.
let revision = 0;

export function readLinkActionCode(search) {
	return new URLSearchParams(search || '').get('oobCode');
}

/**
 * True while the URL carries a code that has not finished being spent — whether
 * nothing has picked it up yet or an exchange is still in flight. Read by
 * `AuthRoute` to hold its redirect open for exactly that long.
 */
export function hasUnspentLinkAction(search) {
	const code = readLinkActionCode(search);

	return !!code && !spentCodes.has(code);
}

/**
 * Called by the screen that spends a code, once the attempt has settled either
 * way. Releases the route to resume its ordinary business and wakes it up to
 * notice, since nothing about the URL or the session need have changed.
 *
 * A reset code is deliberately never marked. Its screen has a result to show
 * ("Password reset successful") and no sign-in to trigger a redirect with, so
 * letting the route take over would only snatch that message away.
 */
export function markLinkActionSpent(code) {
	if (!code || spentCodes.has(code)) {
		return;
	}

	spentCodes.add(code);
	revision += 1;

	listeners.forEach((listener) => listener());
}

/** Subscription half of the `useSyncExternalStore` contract. */
export function subscribeToLinkActions(listener) {
	listeners.add(listener);

	return () => {
		listeners.delete(listener);
	};
}

/** Snapshot half of the same contract — see `revision`. */
export function getLinkActionRevision() {
	return revision;
}
